import { type InitFlowComponent, RewardPoints, type TranslationIDs, useIntl } from '@capsicum/common';
import { BoldText, Button, Image } from '@capsicum/common-web';
import { useGreetingsFlowVisibility, useMe } from '@capsicum/socios-core';
import { type ReactNode, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import { Shell } from '../../ui';

export const GreetingsFlow: InitFlowComponent = ({ fallback, isReactive, next }) => {
	const { me } = useMe();
	const { $t } = useIntl();

	const { isVisible, setVisibility } = useGreetingsFlowVisibility();

	useEffect(() => {
		if (!isReactive) next();
	}, [isReactive, next]);

	const onContinue = useCallback(() => {
		setVisibility(false);
	}, [setVisibility]);

	const translate = useCallback(
		(translationID: TranslationIDs) => {
			return $t(translationID, {
				ssuLogo: () => <S.StyledImage alt="SSU Logo" height={24} src={RewardPoints} width={32} />,
				strong: (chunks: Array<ReactNode>) => <BoldText>{chunks}</BoldText>
			});
		},
		[$t]
	);

	return isVisible === false ? (
		fallback
	) : (
		<S.Wrapper>
			{isVisible && (
				<Shell
					buttons={[
						<Button
							fullWidth
							key="confirm"
							testID="greetingsFlow-continue-button"
							text={$t('onboard.greetingScreen.actions.continue')}
							onClick={onContinue}
						/>
					]}
				>
					<S.Content>
						<S.Header>
							<S.Title>
								{$t('onboard.greetingScreen.heading', {
									name: () => <S.FullName>{me.username}</S.FullName>
								})}
							</S.Title>

							<S.SubTitle>{$t('onboard.greetingScreen.subHeading')}</S.SubTitle>
						</S.Header>

						<S.Steps>
							<S.Step>
								<S.StepTitle>{$t('onboard.greetingScreen.stepOne.title')}</S.StepTitle>

								<S.StepDescriptions>
									<S.StepDescription>
										{translate('onboard.greetingScreen.stepOne.description')}
									</S.StepDescription>
								</S.StepDescriptions>
							</S.Step>

							<S.Step>
								<S.StepTitle>{$t('onboard.greetingScreen.stepTwo.title')}</S.StepTitle>

								<S.StepDescriptions>
									<S.StepDescription>
										{translate('onboard.greetingScreen.stepTwo.description')}
									</S.StepDescription>
								</S.StepDescriptions>
							</S.Step>

							<S.Step>
								<S.StepTitle>{$t('onboard.greetingScreen.stepThree.title')}</S.StepTitle>

								<S.StepDescriptions>
									<S.StepDescription>
										{translate('onboard.greetingScreen.stepThree.descriptionOne')}
									</S.StepDescription>

									<S.StepDescription>
										{translate('onboard.greetingScreen.stepThree.descriptionTwo')}
									</S.StepDescription>
								</S.StepDescriptions>
							</S.Step>
						</S.Steps>
					</S.Content>
				</Shell>
			)}
		</S.Wrapper>
	);
};

GreetingsFlow.keepAlive = true;

namespace S {
	export const Wrapper = styled.div`
		height: 100%;
		display: flex;
		overflow-y: auto;
		align-items: center;
		justify-content: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const Header = styled.header`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const Title = styled.span`
		text-transform: uppercase;
		${({ theme }) => theme.fonts.headings.display2};
	`;

	export const FullName = styled(Title)`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const SubTitle = styled.h2`
		margin: 0;
		padding: 0;
		${({ theme }) => theme.fonts.headings.title5};
	`;

	export const StyledImage = styled(Image)`
		display: inline;
		vertical-align: bottom;
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const Steps = styled.div`
		display: flex;
		flex-direction: column;
	`;

	export const Step = styled.article`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		padding-bottom: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const StepTitle = styled.h3`
		margin: 0;
		padding: 0;
		${({ theme }) => theme.fonts.headings.title6};
	`;

	export const StepDescriptions = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
	`;

	export const StepDescription = styled.p`
		margin: 0;
		padding: 0;
		${({ theme }) => theme.fonts.text.text3};
		margin-left: ${({ theme }) => theme.spacings.medium};
	`;
}
